import { createFileRoute, Link } from "@tanstack/react-router";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { useWishlist } from "@/hooks/storefront/use-wishlist";
import { useCart } from "@/hooks/storefront/use-cart";
import { formatPrice, productSlug } from "@/lib/format";
import { ProductImage } from "@/components/storefront/ProductImage";
import { Heart, ShoppingCart, X } from "lucide-react";
import { useRef, useState } from "react";
import { CART_DRAG_MIME, flyToCart, hideCartDropHint, serializeDrag, setPlayfulProductDragImage, showCartDropHint } from "@/lib/fly-to-cart";

export const Route = createFileRoute("/_authenticated/wishlist")({
  head: () => ({ meta: [
      { property: "og:url", content: "https://mrpanda.gr/wishlist" },{ title: "Τα αγαπημένα μου — MrPanda.gr" }] }),
  component: WishlistPage,
});

function WishlistPage() {
  const { items, remove } = useWishlist();
  const cart = useCart();
  const [dragId, setDragId] = useState<string | null>(null);
  const imgRefs = useRef<Record<string, HTMLElement | null>>({});

  const addToCart = (it: (typeof items)[number]) => {
    cart.add({ productId: it.productId, psId: it.psId, name: it.name, price: it.price, imageUrl: it.imageUrl, originalPrice: it.originalPrice ?? null });
  };

  const addWithFly = (it: (typeof items)[number]) => {
    const rect = imgRefs.current[it.productId]?.getBoundingClientRect();
    if (rect) {
      flyToCart({ fromRect: rect, imageUrl: it.imageUrl, onArrive: () => addToCart(it) });
    } else {
      addToCart(it);
    }
  };

  return (
    <StorefrontLayout>
      <div className="mx-auto max-w-none px-4 pb-12 pt-6">
        {/* Hero header */}
        <div className="mp-playful-panel relative mb-8 overflow-hidden rounded-3xl border border-border p-8 text-center shadow-sm">
          <div className="pointer-events-none absolute -left-4 top-2 select-none text-5xl opacity-20 animate-bounce">🐼</div>
          <div className="pointer-events-none absolute right-4 top-6 select-none text-3xl opacity-20" style={{ animationDelay: "0.4s" }}>✨</div>
          <div className="mb-3 inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Heart className="h-8 w-8 fill-current" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight">Τα αγαπημένα μου</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {items.length > 0
              ? `Έχεις ${items.length} αγαπημέν${items.length === 1 ? "ο προϊόν" : "α προϊόντα"} 💖`
              : "Μάζεψε εδώ ό,τι σου αρέσει 💖"}
          </p>
        </div>

        {items.length === 0 ? (
          <div className="mp-playful-panel mx-auto max-w-md rounded-3xl border border-border p-12 text-center">
            <div className="mb-3 text-6xl">🐼💔</div>
            <h2 className="text-lg font-bold">Η λίστα σου είναι άδεια</h2>
            <p className="mt-1 text-sm text-muted-foreground">Ανακάλυψε προϊόντα και πάτα την καρδούλα για να τα αποθηκεύσεις.</p>
            <Link
              to="/"
              className="mp-bouncy mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm"
            >
              🛍️ Ξεκίνα τις αγορές
            </Link>
          </div>
        ) : (
          <>
            <p className="mb-6 hidden items-center justify-center gap-2 text-center text-sm font-medium text-muted-foreground lg:flex">
              <ShoppingCart className="h-4 w-4" />
              Σύρε ένα προϊόν στο καλάθι δεξιά ή πάτησε «Στο καλάθι» 🛒
            </p>

            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {items.map((it, idx) => (
              <li
                key={it.productId}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = "copy";
                  setPlayfulProductDragImage(e.dataTransfer, {
                    productId: it.productId,
                    psId: it.psId,
                    name: it.name,
                    price: it.price,
                    imageUrl: it.imageUrl,
                  });
                  e.dataTransfer.setData(
                    CART_DRAG_MIME,
                    serializeDrag({
                      productId: it.productId,
                      psId: it.psId,
                      name: it.name,
                      price: it.price,
                      imageUrl: it.imageUrl,
                      originalPrice: it.originalPrice ?? null,
                    }),
                  );
                  e.dataTransfer.setData("text/plain", it.name);
                  setDragId(it.productId);
                  showCartDropHint();
                }}
                onDragEnd={() => {
                  setDragId(null);
                  hideCartDropHint();
                }}
                className={`group relative flex animate-fade-in cursor-grab flex-col rounded-lg border bg-card p-3 transition hover:shadow-md active:cursor-grabbing ${
                  it.originalPrice != null && it.originalPrice > it.price
                    ? "mp-gold-card border-yellow-400/60"
                    : "border-border hover:border-primary/40"
                } ${dragId === it.productId ? "scale-95 opacity-50 ring-2 ring-primary" : ""}`}
                style={{ animationDelay: `${Math.min(idx * 60, 480)}ms`, animationFillMode: "backwards" }}
              >
                {it.originalPrice != null && it.originalPrice > it.price && (
                  <>
                    <span className="mp-sparkle" style={{ top: "6px", left: "10px" }}>✨</span>
                    <span className="mp-sparkle" style={{ top: "40%", right: "12px", animationDelay: "0.6s" }}>⭐</span>
                    <span className="mp-sparkle" style={{ bottom: "64px", left: "16px", animationDelay: "1.1s" }}>✨</span>
                  </>
                )}
                <button
                  onClick={() => remove(it.productId)}
                  aria-label="Αφαίρεση από τα αγαπημένα"
                  className="absolute right-2 top-2 z-10 grid h-9 w-9 place-items-center rounded-full bg-background/95 text-muted-foreground shadow-md backdrop-blur transition-all duration-200 hover:bg-destructive hover:text-destructive-foreground hover:scale-110"
                >
                  <X className="h-4 w-4" />
                </button>

                <Link
                  to="/p/$slug"
                  params={{ slug: productSlug(it.name, it.psId, it.productId) }}
                  ref={(el) => { imgRefs.current[it.productId] = el; }}
                  className="relative mb-3 block aspect-square overflow-hidden rounded-md bg-white"
                >
                  <ProductImage
                    src={it.imageUrl}
                    alt={it.name}
                    width={220}
                    className="h-full w-full object-contain p-2 transition duration-500 group-hover:scale-105"
                    draggable={false}
                    fallback={<div className="grid h-full w-full place-items-center text-5xl">🐼</div>}
                  />
                </Link>

                <div className="flex flex-1 flex-col">
                  <Link
                    to="/p/$slug"
                    params={{ slug: productSlug(it.name, it.psId, it.productId) }}
                    className="text-sm font-medium text-foreground group-hover:text-primary line-clamp-2"
                  >
                    {it.name}
                  </Link>
                  <div className="mt-auto pt-3">
                    <div className="flex items-center gap-2">
                      <div className="flex flex-1 flex-col">
                        {it.originalPrice != null && it.originalPrice > it.price && (
                          <span className="text-xs text-muted-foreground line-through">{formatPrice(it.originalPrice)}</span>
                        )}
                        <span className={`font-bold ${it.originalPrice != null && it.originalPrice > it.price ? "text-destructive" : "text-price"}`}>
                          {formatPrice(it.price)}
                        </span>
                      </div>
                      <button
                        onClick={() => addWithFly(it)}
                        aria-label="Προσθήκη στο καλάθι"
                        title="Προσθήκη στο καλάθι"
                        className="mp-bouncy grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-primary text-primary-foreground shadow-md transition hover:scale-110"
                      >
                        <ShoppingCart className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
            </ul>
          </>
        )}
      </div>
    </StorefrontLayout>
  );
}
